import React, { useState, useEffect, useMemo } from 'react';
import { Table, Input, DatePicker, Select, Spin, Alert } from 'antd';
import type { TableProps } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getRequest, getRequestWithParams } from '../../../api/httpRequest';

const { RangePicker } = DatePicker;

interface OrderData {
  key: number;
  no: number;
  date: string;
  type: string;
  order: string;
}

const OrdersTab: React.FC = () => {
  const [data, setData] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [dateFilter, setDateFilter] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [orderFilter, setOrderFilter] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
      setLoading(true);

      getRequest("/server/proxy/zik/GetOrders")
        .then(resp => {
          const formattedData: OrderData[] = resp.data.map((item: any, index: number) => ({
            key: index,
            no: item.no,
            date: dayjs(item.date).format('DD.MM.YYYY'),
            type: item.type,
            order: item.order
          }));
      
          setData(formattedData);
          setError(null);
          setLoading(false);
      })
      .catch(error => {
        console.log(error)
        alert(error)
        setError(error instanceof Error ? error.message : 'Unknown error');
        setLoading(false);
      })
    
  };

  const filteredData = useMemo(() => {
    return data.filter(item => {
      if (dateFilter) {
        const [startDate, endDate] = dateFilter;
        const itemDate = dayjs(item.date, 'DD.MM.YYYY');
        const startTimestamp = startDate.startOf('day').valueOf();
        const endTimestamp = endDate.endOf('day').valueOf();
        const itemTimestamp = itemDate.valueOf();
        
        if (itemTimestamp < startTimestamp || itemTimestamp > endTimestamp) {
          return false;
        }
      }
      
      if (typeFilter && !item.type.toLowerCase().includes(typeFilter.toLowerCase())) {
        return false;
      }
      
      if (orderFilter && !item.order.toLowerCase().includes(orderFilter.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  }, [data, dateFilter, typeFilter, orderFilter]);

  const typeOptions = useMemo(() => {
    const types = Array.from(new Set(data.map(item => item.type)));
    return types.map(type => ({ value: type, label: type }));
  }, [data]);

  const handleDateChange = (dates: any) => {
    setDateFilter(dates);
  };

  const handleTypeChange = (value: string | null) => {
    setTypeFilter(value);
  };

  const handleOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderFilter(e.target.value || null);
  };

  const columns: TableProps<OrderData>['columns'] = [
    {
      title: 'Номер',
      dataIndex: 'no',
      key: 'no',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.no - b.no,
      showSorterTooltip: { title: 'Сортировать по номеру' }
    },
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      align: 'center',
      sorter: (a, b) => {
        const dateA = dayjs(a.date, 'DD.MM.YYYY').valueOf();
        const dateB = dayjs(b.date, 'DD.MM.YYYY').valueOf();
        return dateA - dateB;
      },
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            format="DD.MM.YYYY"
            value={dateFilter}
            onChange={(dates) => {
              handleDateChange(dates);
              close();
            }}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            style={{ width: 250 }}
          />
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      filtered: !!dateFilter
    },
    {
      title: 'Вид приказа',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Выберите вид"
            style={{ width: 200 }}
            value={typeFilter}
            onChange={(value) => {
              handleTypeChange(value);
              close();
            }}
            allowClear
            showSearch
            optionFilterProp="label"
            options={typeOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      filtered: !!typeFilter,
      onFilter: (value, record) => record.type === value
    },
    {
      title: 'Приказ',
      dataIndex: 'order',
      key: 'order',
      ellipsis: { showTitle: true },
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Поиск по приказу"
            value={orderFilter || ''}
            onChange={(e) => {
              handleOrderChange(e);
            }}
            onPressEnter={() => close()}
            style={{ width: 200 }}
          />
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      filtered: !!orderFilter
    }
  ];

  if (error) {
    return (
      <Alert
        message="Ошибка загрузки данных"
        description={error}
        type="error"
        showIcon
        action={
          <button
            onClick={fetchData}
            style={{ marginTop: '8px', padding: '8px 16px', cursor: 'pointer' }}
          >
            Повторить попытку
          </button>
        }
      />
    );
  }

  return (
    <Spin spinning={loading} tip="Загрузка данных..." size="large">
      
      <Table<OrderData>
        columns={columns}
        dataSource={filteredData}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Всего записей: ${total}`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        scroll={{ x: 'max-content' }}
        size="middle"
        bordered
        showSorterTooltip={{ target: 'sorter-icon' }}
      />
    </Spin>
  );
};

export default OrdersTab;